<template>
  <v-row class="posts-list">
    <v-col cols="12">
      <v-row no-gutters justify="center">
        <v-col cols="12" class="posts-list__wrapper">
          <div class="min-h">
            <v-row class="mx-0" v-if="loaded">
              <v-col cols="12" class="w-100 d-flex align-center pb-0 px-0">
                <v-text-field
                  v-model="postsSearchInputValue"
                  class="posts-list__search-field"
                  :label="$t('message.posts.list.searchFieldPlaceholder')"
                  prepend-inner-icon="icon-search"
                  clearable
                  outlined
                  rounded
                  :hide-details="true"
                  @input="onPostsSearch"
                ></v-text-field>
              </v-col>

              <v-col class="py-2 px-0">
                <div class="d-flex">
                  <Pagination
                    :page="page"
                    :per-page="perPage"
                    :options="perPageOptions"
                    :total-count="totalCount"
                    :disabled="loading.list"
                    @change="handleChange"
                  ></Pagination>
                  <div class="selected-date" v-if="isDateSelected">
                    <span class="selected-date__label">
                      {{ $t("message.posts.planning.postsFrom") }}:
                      <b>{{ date[0] }}</b>
                    </span>
                    <ui-button outlined color="primary" @click="clearCalendar">
                      {{ $t("message.posts.planning.showAllPlannedPosts") }}
                    </ui-button>
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-row class="posts-list__list-wrapper">
              <v-col cols="8" xl="9">
                <template v-if="loaded && posts.length > 0">
                  <PostListPlannedItem
                    v-for="post in posts"
                    :key="post.id"
                    :post="post"
                    class="posts-list__item"
                    @deletePost="deletePost"
                  />
                </template>
                <div v-else class="empty-text">
                  {{ $t("message.posts.planning.emptyText") }}
                </div>
                <SectionLoader :status="loading.list" :opacity="0.75" />
              </v-col>
              <v-col cols="4" xl="3">
                <PostCalendar
                  :value="calendarData"
                  ref="calendarContainer"
                  class="calendar"
                  @input="onDateChange"
                />
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
      <SectionLoader :status="loading.view" :opacity="1" />
    </v-col>
  </v-row>
</template>

<script>
import PostListPlannedItem from "@/components/Posts/PostListItem/PostListPlannedItem.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import Pagination from "@/components/List/Pagination";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import { gtmEngagement as gtmEngagementMixin } from "@/helpers/mixins/gtm-engagement";
import { addPostMixin } from "@/helpers/mixins/add-post";
import { dateRangeFilterMixin } from "@/helpers/mixins/date-range-filter";
import { videoTutorialsMixin } from "@/helpers/mixins/video-tutorials";
import PostCalendar from "@/components/Posts/PostCalendar.vue";
import postsService from "@/services/gmbPosts";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import serviceHandler from "@/helpers/handlers/service";

export default {
  name: "PostsListSent",
  components: {
    PostListPlannedItem,
    SectionLoader,
    Pagination,
    PostCalendar
  },
  mixins: [
    permissionsInfo,
    gtmEngagementMixin,
    dateRangeFilterMixin,
    videoTutorialsMixin,
    addPostMixin
  ],
  data() {
    return {
      calendarData: [],
      isDateSelected: false,
      startRectPosition: 0
    };
  },
  async created() {
    await this.getCalendarData();
  },
  async mounted() {
    this.loading.view = true;
    await this.fetchPosts();
    this.loading.view = false;
    this.mixin__setGtmEngagementTimeouts();
    this.startRectPosition =
      this.$refs["calendarContainer"].$el.getBoundingClientRect().top / 1.75;

    window.addEventListener("scroll", this.handleScroll, false);
  },
  destroyed() {
    window.removeEventListener("scroll", this.handleScroll, false);
  },
  methods: {
    handleScroll() {
      if (window.pageYOffset > this.startRectPosition) {
        document.body.style.setProperty(
          "--scroll",
          window.pageYOffset - this.startRectPosition + "px"
        );
      } else {
        document.body.style.setProperty("--scroll", 0);
      }
    },
    clearCalendar() {
      this.$refs.calendarContainer.inputHandler();
    },
    async getCalendarData() {
      const localCalendarData = await serviceHandler(
        postsService.getPostsScheduleCalendar()
      );
      this.calendarData = localCalendarData.map(({ date }) => date);
    },
    async onDateChange(date) {
      this.isDateSelected = !!date;
      this.page = 1;
      this.date = [date, date];
      await this.fetchPosts();
    },
    async fetchPosts() {
      const response = await serviceHandler(
        postsService.getPostsSchedule(this.params)
      );

      if (response && response.results) {
        this.posts = [...response.results];
        this.totalCount = response.count;
      }
      this.loaded = true;
    },
    async deletePost(params) {
      this.loading.list = true;

      const response = await postsService.deleteScheduledPost(params.id);

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.POSTS_MASS_DELETE,
        num_locations: params.numLocations || null,
        post_type: params.postType || null,
        publish_date: params.publishDate || null
      });

      if (response && response.status === 204) {
        this.$notify({
          group: "default",
          type: "success",
          text: this.$t("message.posts.successfullyDeleted")
        });
        await this.fetchPosts();
        await this.getCalendarData();
      }
      this.loading.list = false;
    }
  }
};
</script>
<style lang="scss" scoped>
.selected-date {
  display: flex;
  align-items: center;

  &__label {
    margin-left: $spacing * 5;
    margin-right: $spacing * 2.5;
    @include font-size(14px);
    color: $gray;
    text-decoration: underline;
  }
}

.posts-list__wrapper {
  min-height: 750px;
}
.calendar {
  transform: translateY(var(--scroll));
}
div.posts-list__search-field {
  max-width: 100%;
}
.empty-text {
  color: $gray-2;
  margin-top: $spacing * 2;
}
</style>
